import React, { useState, useEffect } from 'react';
import { RefreshCw, CheckCircle2, AlertTriangle, Database, Cloud } from 'lucide-react';
import axios from 'axios';
import { getApiBaseUrl } from '../services/api';

interface SyncStatus {
  collectionId?: string;
  databaseFaceCount: number;
  rekognitionFaceCount: number;
  isInSync: boolean;
  orphanedFacesCount: number;
  missingFacesCount: number;
  orphanedFaceIds: string[];
  missingFaceIds: string[];
  rekognitionAvailable?: boolean;
  rekognitionErrorMessage?: string;
}

const FaceSyncStatus: React.FC = () => {
  const [status, setStatus] = useState<SyncStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const api = axios.create({
        baseURL: getApiBaseUrl(),
        headers: {
          'Content-Type': 'application/json',
        },
      });

      // Get auth token from localStorage
      const token = localStorage.getItem('authToken');
      if (token) {
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }

      const response = await api.get('/face-recognition/sync-status');
      setStatus(response.data);
    } catch (err: any) {
      console.error('Error fetching sync status:', err);
      setError(err?.response?.data?.message || err?.message || 'Failed to fetch face sync status');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchStatus();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Loading face count details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <AlertTriangle className="w-8 h-8 mx-auto mb-4 text-red-600" />
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={handleRefresh}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Face Sync Status</h1>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {status && (
        <>
          {/* Rekognition unavailable warning */}
          {status.rekognitionAvailable === false && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 flex items-start gap-3 mb-6">
              <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-amber-900">AWS Rekognition Unavailable</h3>
                <p className="text-sm text-amber-800 mt-1">
                  {status.rekognitionErrorMessage || 'Unable to connect to AWS Rekognition.'} Check your AWS configuration (credentials, region, and Rekognition collection).
                </p>
                <p className="text-sm text-amber-700 mt-2">Showing database face count only.</p>
              </div>
            </div>
          )}

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">Faces in Rekognition</p>
                  <p className="text-3xl font-bold text-gray-900">
                    {status.rekognitionAvailable === false ? '—' : status.rekognitionFaceCount}
                  </p>
                </div>
                <Cloud className="w-12 h-12 text-blue-500" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">Faces in Database</p>
                  <p className="text-3xl font-bold text-gray-900">{status.databaseFaceCount}</p>
                </div>
                <Database className="w-12 h-12 text-green-500" />
              </div>
            </div>
          </div>

          {/* Sync Status */}
          <div
            className={`rounded-lg shadow-md p-6 mb-6 ${
              status.rekognitionAvailable === false
                ? 'bg-amber-50 border border-amber-200'
                : status.isInSync
                ? 'bg-green-50 border border-green-200'
                : 'bg-yellow-50 border border-yellow-200'
            }`}
          >
            <div className="flex items-center gap-3 mb-2">
              {status.rekognitionAvailable === false ? (
                <AlertTriangle className="w-6 h-6 text-amber-600" />
              ) : status.isInSync ? (
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              ) : (
                <AlertTriangle className="w-6 h-6 text-yellow-600" />
              )}
              <h2 className="text-xl font-bold text-gray-900">
                {status.rekognitionAvailable === false
                  ? 'Rekognition Unavailable'
                  : status.isInSync
                  ? 'In Sync'
                  : 'Out of Sync'}
              </h2>
            </div>
            <p className="text-gray-700">
              {status.rekognitionAvailable === false
                ? 'AWS Rekognition could not be reached. Database face count is shown above.'
                : status.isInSync
                ? 'All faces are properly synchronized between Rekognition and the database.'
                : 'There are discrepancies between Rekognition and the database.'}
            </p>
          </div>

          {/* Discrepancies - only when Rekognition is available and out of sync */}
          {status.rekognitionAvailable !== false && !status.isInSync && (
            <div className="space-y-4 mb-6">
              {status.orphanedFacesCount > 0 && (
                <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-orange-500">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    Orphaned Faces: {status.orphanedFacesCount}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    These faces exist in Rekognition but are not linked to any active employee in the database.
                  </p>
                  {status.orphanedFaceIds.length > 0 && (
                    <div className="mt-4">
                      <p className="text-sm font-medium text-gray-700 mb-2">Face IDs:</p>
                      <div className="bg-gray-50 rounded p-3 max-h-40 overflow-y-auto">
                        {status.orphanedFaceIds.map((faceId, index) => (
                          <code key={index} className="block text-xs text-gray-600 font-mono mb-1">
                            {faceId}
                          </code>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {status.missingFacesCount > 0 && (
                <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-red-500">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    Missing Faces: {status.missingFacesCount}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    These FaceIds exist in the database but the faces are missing from Rekognition collection.
                  </p>
                  {status.missingFaceIds.length > 0 && (
                    <div className="mt-4">
                      <p className="text-sm font-medium text-gray-700 mb-2">Face IDs:</p>
                      <div className="bg-gray-50 rounded p-3 max-h-40 overflow-y-auto">
                        {status.missingFaceIds.map((faceId, index) => (
                          <code key={index} className="block text-xs text-gray-600 font-mono mb-1">
                            {faceId}
                          </code>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Details Table */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Details</h2>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <span className="text-gray-700">Rekognition Face Count:</span>
                <span className="font-semibold text-gray-900">
                  {status.rekognitionAvailable === false ? '—' : status.rekognitionFaceCount}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <span className="text-gray-700">Database Face Count:</span>
                <span className="font-semibold text-gray-900">{status.databaseFaceCount}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <span className="text-gray-700">Orphaned Faces:</span>
                <span className="font-semibold text-gray-900">{status.orphanedFacesCount}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <span className="text-gray-700">Missing Faces:</span>
                <span className="font-semibold text-gray-900">{status.missingFacesCount}</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-gray-700">Sync Status:</span>
                <span
                  className={`font-semibold ${
                    status.rekognitionAvailable === false
                      ? 'text-amber-600'
                      : status.isInSync
                      ? 'text-green-600'
                      : 'text-yellow-600'
                  }`}
                >
                  {status.rekognitionAvailable === false
                    ? 'Rekognition Unavailable'
                    : status.isInSync
                    ? 'In Sync'
                    : 'Out of Sync'}
                </span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default FaceSyncStatus;


